import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { MapPin, Calendar, CreditCard, QrCode, ArrowRight, ChevronLeft, ChevronRight, Users, Wifi } from 'lucide-react'
import { Link } from 'react-router-dom'

// 10张真实小程序截图
const screenshots = [
  { src: '/images/venues/Screenshot_2026-03-26-10-53-41-034_com.tencent.mm_1774493883979edit.jpg', label: '场馆列表' },
  { src: '/images/venues/Screenshot_2026-03-26-10-53-45-739_com.tencent.mm_1774493867333edit.jpg', label: '地图导航' },
  { src: '/images/venues/Screenshot_2026-03-26-10-53-56-741_com.tencent.mm-edit.jpg', label: '时段选择' },
  { src: '/images/venues/Screenshot_2026-03-26-10-54-35-903_com.tencent.mm.jpg', label: '场馆详情' },
  { src: '/images/venues/Screenshot_2026-03-26-10-54-41-730_com.tencent.mm_1774493841540edit.jpg', label: '价格日历' },
  { src: '/images/venues/Screenshot_2026-03-26-10-55-00-426_com.tencent.mm-edit.jpg', label: '订单确认' },
  { src: '/images/venues/Screenshot_2026-03-26-10-55-29-727_com.tencent.mm.jpg', label: '支付完成' },
  { src: '/images/venues/Screenshot_2026-03-26-10-55-39-315_com.tencent.mm.jpg', label: '订单记录' },
  { src: '/images/venues/Screenshot_2026-03-26-10-55-46-187_com.tencent.mm.jpg', label: '扫码签到' },
  { src: '/images/venues/Screenshot_2026-03-26-10-56-06-857_com.tencent.mm-edit.jpg', label: '个人中心' },
]

const features = [
  {
    icon: MapPin,
    title: '附近场馆',
    desc: '一键定位附近网球场馆，地图+列表双视图，导航直达',
    color: 'rgba(16,185,129,0.15)',
    textColor: '#34d399',
  },
  {
    icon: Calendar,
    title: '实时预约',
    desc: '时段余量实时更新，约满自动提醒，不跑空',
    color: 'rgba(59,130,246,0.15)',
    textColor: '#60a5fa',
  },
  {
    icon: CreditCard,
    title: '在线支付',
    desc: '微信支付秒级完成，无需到场排队付款，省时省力',
    color: 'rgba(168,85,247,0.15)',
    textColor: '#a78bfa',
  },
  {
    icon: QrCode,
    title: '扫码签到',
    desc: '到店出示订单二维码，扫码核销入场，云端自动记录',
    color: 'rgba(245,158,11,0.15)',
    textColor: '#fbbf24',
  },
  {
    icon: Users,
    title: '多人组队',
    desc: '一键分享订单链接，约朋友一起打球，自动匹配时间',
    color: 'rgba(239,68,68,0.15)',
    textColor: '#f87171',
  },
  {
    icon: Wifi,
    title: '智能推荐',
    desc: '基于历史预约记录，智能推荐最适合你的场馆和时段',
    color: 'rgba(34,197,94,0.15)',
    textColor: '#4ade80',
  },
]

export default function Venues() {
  const [current, setCurrent] = useState(0)
  const [autoPlay, setAutoPlay] = useState(true)

  useEffect(() => {
    if (!autoPlay) return
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % screenshots.length)
    }, 2800)
    return () => clearInterval(timer)
  }, [autoPlay])

  const prev = () => {
    setAutoPlay(false)
    setCurrent((c) => (c - 1 + screenshots.length) % screenshots.length)
  }
  const next = () => {
    setAutoPlay(false)
    setCurrent((c) => (c + 1) % screenshots.length)
  }

  return (
    <div className="min-h-screen pt-16">
      {/* ===== Hero ===== */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[600px] opacity-15"
          style={{
            background: 'radial-gradient(ellipse, rgba(16,185,129,0.5), transparent 70%)',
            filter: 'blur(60px)',
          }}
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* 文案 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium mb-6"
              style={{
                background: 'rgba(16,185,129,0.15)',
                border: '1px solid rgba(16,185,129,0.3)',
                color: '#34d399',
              }}
            >
              <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              运动订场
            </div>

            <h1 className="text-4xl md:text-6xl font-black text-white mb-6 tracking-tight">
              Lafit 运动订场
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-8">
              一键预约 · 扫码入场 · 告别电话排队
              <br />
              让订场像点外卖一样简单
            </p>

            <Link
              to="/#download"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-bold transition-all duration-300 hover:gap-3"
              style={{
                background: '#10b981',
                boxShadow: '0 0 30px rgba(16,185,129,0.4)',
                color: '#fff',
              }}
            >
              立即预约
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>

          {/* ===== 截图轮播 ===== */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.3 }}
            className="mt-20 flex flex-col items-center"
          >
            <div className="relative flex items-center justify-center gap-6">
              {/* 左侧预览 */}
              <div
                className="hidden md:block w-40 h-72 rounded-2xl overflow-hidden opacity-40 scale-90 cursor-pointer transition-all duration-300 hover:opacity-60"
                style={{ border: '1px solid rgba(255,255,255,0.1)' }}
                onClick={prev}
              >
                <img
                  src={screenshots[(current - 1 + screenshots.length) % screenshots.length].src}
                  alt="prev"
                  className="w-full h-full object-cover object-top"
                />
              </div>

              {/* 手机样机 */}
              <div className="relative">
                <div
                  className="absolute inset-0 rounded-[2.5rem] pointer-events-none z-10"
                  style={{
                    border: '3px solid rgba(255,255,255,0.15)',
                    boxShadow: '0 0 0 1px rgba(0,0,0,0.5), 0 40px 80px rgba(0,0,0,0.6), 0 0 60px rgba(16,185,129,0.2)',
                  }}
                />
                <div
                  className="absolute top-3 left-1/2 -translate-x-1/2 w-24 h-5 rounded-full z-20"
                  style={{ background: 'rgba(0,0,0,0.8)' }}
                />
                <div
                  className="w-64 h-[520px] rounded-[2.5rem] overflow-hidden"
                  style={{ background: '#0a0a0a' }}
                >
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={current}
                      src={screenshots[current].src}
                      alt={screenshots[current].label}
                      className="w-full h-full object-cover object-top"
                      initial={{ opacity: 0, scale: 1.04 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.97 }}
                      transition={{ duration: 0.35 }}
                    />
                  </AnimatePresence>
                </div>
              </div>

              {/* 右侧预览 */}
              <div
                className="hidden md:block w-40 h-72 rounded-2xl overflow-hidden opacity-40 scale-90 cursor-pointer transition-all duration-300 hover:opacity-60"
                style={{ border: '1px solid rgba(255,255,255,0.1)' }}
                onClick={next}
              >
                <img
                  src={screenshots[(current + 1) % screenshots.length].src}
                  alt="next"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* 标签 */}
            <div className="mt-5 text-sm font-medium" style={{ color: '#34d399' }}>
              {screenshots[current].label}
            </div>

            {/* 控制 */}
            <div className="mt-4 flex items-center gap-4">
              <button
                onClick={prev}
                className="p-2 rounded-full transition-all hover:scale-110"
                style={{ background: 'rgba(255,255,255,0.08)', color: '#9ca3af' }}
              >
                <ChevronLeft className="w-5 h-5" />
              </button>

              <div className="flex gap-2">
                {screenshots.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => { setAutoPlay(false); setCurrent(i) }}
                    className="rounded-full transition-all duration-300"
                    style={{
                      width: i === current ? '20px' : '6px',
                      height: '6px',
                      background: i === current ? '#10b981' : 'rgba(255,255,255,0.2)',
                    }}
                  />
                ))}
              </div>

              <button
                onClick={next}
                className="p-2 rounded-full transition-all hover:scale-110"
                style={{ background: 'rgba(255,255,255,0.08)', color: '#9ca3af' }}
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            <p className="mt-3 text-xs text-gray-600">
              {autoPlay ? '自动播放中 · 点击箭头手动切换' : '手动模式 · 自动播放已暂停'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* ===== 特性网格 ===== */}
      <section className="py-24 relative">
        <div className="absolute inset-0 grid-bg opacity-10" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-black text-white mb-4">核心功能</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              从找场馆到扫码入场，全流程在线化，体验流畅
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                viewport={{ once: true }}
                className="p-6 rounded-2xl group hover:scale-[1.02] transition-all duration-300"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.04), rgba(255,255,255,0.01))',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                  style={{ background: feature.color, color: feature.textColor }}
                >
                  <feature.icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 截图瀑布流 ===== */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-10" />
        <div
          className="absolute top-0 left-0 w-full h-32 pointer-events-none"
          style={{ background: 'linear-gradient(to bottom, rgba(10,10,20,1), transparent)' }}
        />
        <div
          className="absolute bottom-0 left-0 w-full h-32 pointer-events-none"
          style={{ background: 'linear-gradient(to top, rgba(10,10,20,1), transparent)' }}
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-black text-white mb-4">应用截图</h2>
            <p className="text-gray-400">真实界面展示，所见即所得</p>
          </motion.div>

          <div className="overflow-hidden space-y-4">
            <motion.div
              className="flex gap-4"
              animate={{ x: [0, -1200] }}
              transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
              style={{ width: 'max-content' }}
            >
              {[...screenshots, ...screenshots].map((s, i) => (
                <div
                  key={i}
                  className="flex-shrink-0 w-36 h-64 rounded-2xl overflow-hidden"
                  style={{ border: '1px solid rgba(255,255,255,0.08)' }}
                >
                  <img src={s.src} alt={s.label} className="w-full h-full object-cover object-top" />
                </div>
              ))}
            </motion.div>

            <motion.div
              className="flex gap-4"
              animate={{ x: [0, 1200] }}
              transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
              style={{ width: 'max-content' }}
            >
              {[...screenshots.slice(5), ...screenshots, ...screenshots.slice(0, 5)].map((s, i) => (
                <div
                  key={i}
                  className="flex-shrink-0 w-36 h-64 rounded-2xl overflow-hidden"
                  style={{ border: '1px solid rgba(255,255,255,0.08)' }}
                >
                  <img src={s.src} alt={s.label} className="w-full h-full object-cover object-top" />
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== CTA（带小程序码） ===== */}
      <section className="py-24 relative overflow-hidden">
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(135deg, rgba(16,185,129,0.1), rgba(59,130,246,0.08))' }}
        />
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-black text-white mb-6">
              告别电话订场，扫码即用
            </h2>
            <p className="text-gray-400 mb-8 max-w-xl mx-auto text-lg">
              覆盖全国数百家网球场馆，随时随地轻松预约
            </p>

            {/* 小程序码 */}
            <div className="flex justify-center gap-10 items-start flex-wrap">
              <div className="flex flex-col items-center gap-3">
                <div className="w-40 h-40 rounded-2xl overflow-hidden shadow-2xl"
                  style={{ border: '3px solid rgba(16,185,129,0.4)', boxShadow: '0 0 40px rgba(16,185,129,0.3)' }}>
                  <img
                    src="/images/venues/qr.jpg"
                    alt="Lafit运动订场 小程序码"
                    className="w-full h-full object-cover"
                  />
                </div>
                <span className="text-sm text-gray-400">扫码预约</span>
              </div>
            </div>

            <div className="mt-10">
              <Link
                to="/#download"
                className="inline-flex items-center gap-2 px-10 py-4 rounded-xl font-bold transition-all duration-300 hover:gap-4 hover:scale-105"
                style={{
                  background: '#10b981',
                  boxShadow: '0 0 40px rgba(16,185,129,0.5)',
                  color: '#fff',
                  fontSize: '1.1rem',
                }}
              >
                立即预约
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}
